type Msg = { type: string; [field: string]: unknown };

type Effect =
  | { send: [to: string, msg: Msg] }
  | { later: [ms: number, msg: Msg, key: string] }
  | { cancel: string }
  | { task: [run: () => Promise<Msg>, to: string] };

type Meta = { at: number };

const EFFECTS = Symbol("effects");
type Update<S> = { [EFFECTS]: true; state: S; effects: Effect[] };

type Actor<S, M extends Msg> = {
  init: S;
  receive: (state: S, msg: M, meta: Meta) => S | Update<S>;
};

type Entry = { at: number; to: string; msg: Msg };

type Observed = Entry & { state: unknown };

type Slot = {
  receive: (state: unknown, msg: Msg, meta: Meta) => unknown;
  state: unknown;
  listeners: Set<() => void>;
};

const withEffects = <S>(state: S, ...effects: Effect[]): Update<S> => ({
  [EFFECTS]: true,
  state,
  effects,
});

const unpack = <S>(out: S | Update<S>): Update<S> =>
  typeof out === "object" && out !== null && EFFECTS in out
    ? out
    : withEffects(out as S);

const createSystem = <
  R extends Record<string, Msg> = Record<string, Msg>,
>() => {
  const actors = new Map<string, Slot>();
  const queue: Entry[] = [];
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const observers = new Set<(observed: Observed) => void>();
  let draining = false;
  let stopped = false;

  const clearTimer = (id: string) => {
    clearTimeout(timers.get(id));
    timers.delete(id);
  };

  const perform = (self: string, effect: Effect) => {
    if ("send" in effect) {
      enqueue(effect.send[0], effect.send[1]);
    } else if ("later" in effect) {
      const [ms, msg, key] = effect.later;
      const id = `${self}/${key}`;
      clearTimer(id);
      timers.set(
        id,
        setTimeout(() => {
          timers.delete(id);
          enqueue(self, msg);
        }, ms),
      );
    } else if ("cancel" in effect) {
      clearTimer(`${self}/${effect.cancel}`);
    } else {
      const [run, to] = effect.task;
      Promise.resolve()
        .then(run)
        .then(
          (msg) => enqueue(to, msg),
          (error) => {
            console.error(`[actors] ${self} task failed`, error);
            enqueue(to, { type: "taskFailed" });
          },
        );
    }
  };

  const drain = () => {
    draining = true;
    const changed = new Set<Slot>();
    for (let entry = queue.shift(); entry; entry = queue.shift()) {
      const slot = actors.get(entry.to);
      if (!slot) {
        console.warn(`[actors] no actor "${entry.to}"`, entry.msg);
        continue;
      }
      try {
        const { state, effects } = unpack(
          slot.receive(slot.state, entry.msg, { at: entry.at }),
        );
        if (state !== slot.state) {
          slot.state = state;
          changed.add(slot);
        }
        for (const effect of effects) perform(entry.to, effect);
      } catch (error) {
        console.error(
          `[actors] ${entry.to} failed on "${entry.msg.type}"`,
          error,
        );
      }
      notify({ ...entry, state: slot.state });
    }
    draining = false;
    for (const slot of changed) {
      for (const listener of slot.listeners) listener();
    }
  };

  const notify = (observed: Observed) => {
    for (const observe of observers) {
      try {
        observe(observed);
      } catch (error) {
        console.error("[actors] observer failed", error);
      }
    }
  };

  const enqueue = (to: string, msg: Msg) => {
    if (stopped) return;
    queue.push({ at: Date.now(), to, msg });
    if (!draining) drain();
  };

  const send = <K extends keyof R & string>(to: K, msg: R[K]) =>
    enqueue(to, msg);

  const spawn = <K extends keyof R & string, S>(
    name: K,
    actor: Actor<S, R[K]>,
  ) => {
    actors.set(name, {
      receive: actor.receive as unknown as Slot["receive"],
      state: actor.init,
      listeners: new Set(),
    });
  };

  const getState = <S>(name: keyof R & string) => actors.get(name)?.state as S;

  const subscribe = (name: keyof R & string, listener: () => void) => {
    actors.get(name)?.listeners.add(listener);
    return () => void actors.get(name)?.listeners.delete(listener);
  };

  const onMessage = (observe: (observed: Observed) => void) => {
    observers.add(observe);
    return () => void observers.delete(observe);
  };

  const stop = () => {
    stopped = true;
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    queue.length = 0;
  };

  return { spawn, send, getState, subscribe, onMessage, stop };
};

type System<R extends Record<string, Msg> = Record<string, Msg>> = ReturnType<
  typeof createSystem<R>
>;

export default createSystem;
export type { Actor, Effect, Meta, Msg, Observed, System, Update };
export { withEffects };
